import { FC, Fragment, useContext, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
// components
import ConsentForm from './ConsentForm';
import MainLayout from 'components/common/MainLayout';
import { CommonLoader } from 'components/common/CommonLoader';
// context, interfaces, utils
import { PrivateRouteProps } from 'interfaces';
import { useAuthContext } from 'contexts/AuthContext';
import { UserSubscriptionsContext } from 'contexts/UserSubscriptions';
import { isAdmin, isPlainPrivateRoute, isStaff, isSuperAdmin } from 'lib/helper';
import { AUTH_LINKS, STRIPE_PLANS_ROUTE, STRIPE_SUBSCRIPTION_EXPIRED_ROUTE } from 'constants/index';

const PrivateRoute: FC<PrivateRouteProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { currentUser, isLoading, isLoaded, isLoggedIn, userRoles } = useAuthContext();
  const { userSubscription, hasSubscriptionFetched } = useContext(UserSubscriptionsContext);
  const isAllDataFetched = !isLoading && isLoaded && hasSubscriptionFetched;
  const isSuper = isSuperAdmin(userRoles);
  const isAdminUser = isAdmin(userRoles);
  const isStaffUser = isStaff(userRoles);
  const { id, isAccepted } = currentUser || {};
  const isPlainRoute = isPlainPrivateRoute(location.pathname);

  useEffect(() => {
    if (isAllDataFetched) {
      if (!isSuper) {
        if (!userSubscription && isAdminUser) {
          navigate(STRIPE_PLANS_ROUTE);
        }
        if (!userSubscription && isStaffUser) {
          navigate(STRIPE_SUBSCRIPTION_EXPIRED_ROUTE);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, hasSubscriptionFetched, userSubscription]);

  if (isAllDataFetched) {
    if (id && isLoggedIn) {
      if (!isSuper) {
        if (!isAccepted) {
          return <ConsentForm />;
        }
      }

      if (isPlainRoute) {
        return <Fragment>{children}</Fragment>;
      }
      return <MainLayout>{children}</MainLayout>;
    } else {
      return <Navigate to={AUTH_LINKS.LOGIN_LINK} state={{ from: location }} replace />;
    }
  }

  return <CommonLoader />;
};

export default PrivateRoute;
